import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Bot, Workflow, Network } from 'lucide-react';
import {
  getObservabilityMetrics,
  getPipelinesRun,
  getObservabilityCount,
  getPipelinesByPagination,
  getEntityCount
} from '../services/ObservabilityService';
import { getAgentsPaginated } from '../services/agentService';
import { getPlannersPaginated } from '../services/plannerService';
import { post } from '../utils/apiUtils';
import Pagination from '../components/common/Pagination';
import { usePagination } from '../hooks/usePagination';
import { format, isSameDay } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend } from 'chart.js';
import { Bar, Bubble } from 'react-chartjs-2';
import { getTokenSplit, getCountSplit, getAvgDurationSplit } from '../services/ObservabilityService';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend);

const Container = styled.div`
  padding: 2rem;
  background: #f9fafb;
  min-height: 100vh;
`;

const InlineSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #0066FF;
  border-radius: 50%;
  margin-right: 8px;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.9rem;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const SearchContainer = styled.div`
  margin-left: auto;
  position: relative;
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9rem;
  width: 300px;

  &:focus {
    outline: none;
    border-color: #0066FF;
  }
`;

const TabsContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  overflow-x: auto;
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? '#0066FF' : '#6b7280'};
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-bottom: ${props => props.active ? '2px solid #0066FF' : '2px solid transparent'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.active ? '#0066FF' : '#111827'};
  }
`;

const TabContent = styled.div`
  padding: 1.5rem;
  display: ${props => props.active ? 'block' : 'none'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.9rem;
  color: #374151;
  border-bottom: 1px solid #f3f4f6;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props =>
    props.status === 'Success' || props.status === 'Active' ? '#D1FAE5' :
      props.status === 'Failed' || props.status === 'Inactive' ? '#FEE2E2' :
        props.status === 'Running' ? '#DBEAFE' :
          '#E5E7EB'
  };
  color: ${props =>
    props.status === 'Success' || props.status === 'Active' ? '#065F46' :
      props.status === 'Failed' || props.status === 'Inactive' ? '#991B1B' :
        props.status === 'Running' ? '#1E40AF' :
          '#374151'
  };
`;

const Button = styled.button`
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #0066FF;
  background: transparent;
  border: 1px solid #0066FF;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #EBF5FF;
  }
`;

const AccordionRow = styled.tr`
  background: #f9fafb;
`;

const AccordionCell = styled.td`
  padding: 0 !important;
  border-bottom: 1px solid #e5e7eb !important;
`;

const AccordionContent = styled.div`
  padding: 2rem;
  background: white;
  border-top: 1px solid #e5e7eb;
`;

const GraphsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const GraphCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const GraphTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const GraphContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const TokenGauge = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(
    #22C55E 0deg ${props => props.percentage * 3.6}deg,
    #E5E7EB ${props => props.percentage * 3.6}deg 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 90px;
    height: 90px;
    background: white;
    border-radius: 50%;
  }
`;

const TokenValue = styled.div`
  position: relative;
  z-index: 1;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const TokenLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
`;

const BarChart = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 0;
`;

const SimpleBar = styled.div`
  flex: 1;
  background: ${props => props.color || '#0066FF'};
  border-radius: 4px 4px 0 0;
  height: ${props => props.height || 0}%;
  min-height: 2px;
  position: relative;
  transition: height 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const BarLabel = styled.div`
  font-size: 0.65rem;
  color: #6b7280;
  text-align: center;
  margin-top: 0.25rem;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

const MetricValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: transform 0.2s;
  transform: rotate(${props => props.expanded ? '180deg' : '0deg'});
`;

const ObservabilityDetails = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'agents';
  const selectedId = searchParams.get('id');

  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState(selectedId || null);
  const [expandedRowTab, setExpandedRowTab] = useState(initialTab); // Track which tab the expanded row belongs to
  const [scrollContainerRef, setScrollContainerRef] = useState(null);

  // Date filter: initialize from URL start/end if present, else default to today
  const initialRange = useMemo(() => ([{
    startDate: (() => {
      const s = Number(searchParams.get('start'));
      return Number.isFinite(s) && s > 0 ? new Date(s) : new Date(new Date().setHours(0,0,0,0));
    })(),
    endDate: (() => {
      const e = Number(searchParams.get('end'));
      return Number.isFinite(e) && e > 0 ? new Date(e) : new Date(new Date().setHours(23,59,59,999));
    })(),
    key: 'selection'
  }]), [searchParams]);

  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [draftRange, setDraftRange] = useState(initialRange);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  const dateFilterMs = useMemo(() => {
    const start = appliedRange?.[0]?.startDate ? new Date(appliedRange[0].startDate) : new Date();
    const end = appliedRange?.[0]?.endDate ? new Date(appliedRange[0].endDate) : new Date();
    return { started_at: { $gte: start.getTime(), $lte: end.getTime() } };
  }, [appliedRange]);

  const rangeLabel = useMemo(() => {
    const start = appliedRange?.[0]?.startDate ? new Date(appliedRange[0].startDate) : new Date();
    const end = appliedRange?.[0]?.endDate ? new Date(appliedRange[0].endDate) : new Date();
    const today = new Date();
    const isToday = isSameDay(start, today) && isSameDay(end, today);
    return isToday ? 'Today' : `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
  }, [appliedRange]);

  useEffect(() => {
    const onDown = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    if (showDatePicker) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showDatePicker]);

  useEffect(() => {
    if (showDatePicker) setDraftRange(appliedRange);
  }, [showDatePicker]);

  // Pagination hooks for each tab
  const agentsPagination = usePagination();
  const agentRunsPagination = usePagination();
  const teamsPagination = usePagination();
  const teamRunsPagination = usePagination();
  const plannersPagination = usePagination();
  const plannerRunsPagination = usePagination();

  // Loading states
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentRunsLoading, setAgentRunsLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamRunsLoading, setTeamRunsLoading] = useState(false);
  const [plannersLoading, setPlannersLoading] = useState(false);
  const [plannerRunsLoading, setPlannerRunsLoading] = useState(false);

  // Data states
  const [agents, setAgents] = useState([]);
  const [agentRuns, setAgentRuns] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamRuns, setTeamRuns] = useState([]);
  const [planners, setPlanners] = useState([]);
  const [plannerRuns, setPlannerRuns] = useState([]);

  const [countsLoading, setCountsLoading] = useState(false);
  const [counts, setCounts] = useState({
    totalAgents: 0,
    totalPipelines: 0,
    totalPlanners: 0,
    pipelineRunCount: 0,
    agentRunCount: 0,
    plannerRunCount: 0
  });


  // Graph data states for expanded rows
  const [entityGraphData, setEntityGraphData] = useState({});
  const [graphLoading, setGraphLoading] = useState({});

  // Fetch graph data for specific entity (agent | pipeline | planner) using split endpoints with date filter
  const fetchEntityGraphData = async (entityType, entityId) => {
    const loadingKey = `${entityType}_${entityId}`;
    setGraphLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const filterBody = dateFilterMs || {};
      let tArr = [];
      let cRes, dRes;
      if (entityType === 'agent') {
        [cRes, dRes] = await Promise.all([
          getCountSplit('agent', entityId, filterBody),
          getAvgDurationSplit('agent', entityId, filterBody)
        ]);
      } else {
        const [tResLocal, cResLocal, dResLocal] = await Promise.all([
          getTokenSplit(entityType, entityId, filterBody),
          getCountSplit(entityType, entityId, filterBody),
          getAvgDurationSplit(entityType, entityId, filterBody)
        ]);
        tArr = Array.isArray(tResLocal) ? tResLocal : (tResLocal?.data || []);
        cRes = cResLocal;
        dRes = dResLocal;
      }

      const cArr = Array.isArray(cRes) ? cRes : (cRes?.data || []);
      const dArr = Array.isArray(dRes) ? dRes : (dRes?.data || []);

      // join by metric_type_code
      const map = new Map();
      (tArr || []).forEach((it, idx) => {
        const code = String(
          it.metric_type_code || it.agent_code || it.member_code || it.metric_type_name || it.agent_name || it.tool_name || it.name || `row_${idx}`
        );
        const name = (it.metric_type_name != null && it.metric_type_name !== '')
          ? it.metric_type_name
          : (it.agent_name != null && it.agent_name !== '')
            ? it.agent_name
            : (it.tool_name != null && it.tool_name !== '')
              ? it.tool_name
              : (it.name != null && it.name !== '')
                ? it.name
                : code;
        map.set(code, {
          code,
          name,
          input_tokens: Number(it.input_tokens || 0),
          output_tokens: Number(it.output_tokens || 0),
          total_tokens: Number(it.total_tokens || 0),
        });
      });
      (cArr || []).forEach((it, idx) => {
        const code = String(
          it.metric_type_code || it.agent_code || it.member_code || it.metric_type_name || it.agent_name || it.tool_name || it.name || `row_${idx}`
        );
        const row = map.get(code) || { code, name: (it.metric_type_name || it.agent_name || it.tool_name || it.name || code) };
        row.count = Number(it.count || 0);
        map.set(code, row);
      });
      (dArr || []).forEach((it, idx) => {
        const code = String(
          it.metric_type_code || it.agent_code || it.member_code || it.metric_type_name || it.agent_name || it.tool_name || it.name || `row_${idx}`
        );
        const row = map.get(code) || { code, name: (it.metric_type_name || it.agent_name || it.tool_name || it.name || code) };
        row.avg_duration_ms = Number(it.avg_duration_ms || 0);
        map.set(code, row);
      });

      const joined = Array.from(map.values());
      setEntityGraphData(prev => ({ ...prev, [loadingKey]: { tokenSplit: tArr, countSplit: cArr, durationSplit: dArr, joined } }));
    } catch (error) {
      console.error(`Error fetching ${entityType} graph data:`, error);
      setEntityGraphData(prev => ({ ...prev, [loadingKey]: null }));
    } finally {
      setGraphLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Handle row click to expand/collapse accordion
  const handleRowToggle = (entityType, entityId, stringId) => {
    // Use stringId (agent_id, team_id, planner_id) for comparison if available
    const rowKey = stringId || entityId;
    if (expandedRow === rowKey) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowKey);
      // Ensure the conditional render matches the tab we are expanding in
      if (entityType === 'agent') setExpandedRowTab('agents');
      else if (entityType === 'pipeline') setExpandedRowTab('teams');
      else if (entityType === 'planner') setExpandedRowTab('planners');
      const loadingKey = `${entityType}_${stringId || entityId}`;
      if (!entityGraphData[loadingKey] && !graphLoading[loadingKey]) {
        fetchEntityGraphData(entityType, stringId || entityId);
      }
    }
  };

  // Auto-expand row if ID is in URL
  useEffect(() => {
    if (selectedId) {
      setExpandedRow(selectedId);
      let entityType = '';
      if (activeTab === 'agents') entityType = 'agent';
      else if (activeTab === 'teams') entityType = 'pipeline';
      else if (activeTab === 'planners') entityType = 'planner';

      if (entityType) {
        const loadingKey = `${entityType}_${selectedId}`;
        if (!entityGraphData[loadingKey] && !graphLoading[loadingKey]) {
          fetchEntityGraphData(entityType, selectedId);
        }
      }
    }
  }, [selectedId, activeTab]);

  // Auto-expand row after data is loaded
  useEffect(() => {
    // console.log('Auto-expand effect triggered:', {
    //   selectedId,
    //   activeTab,
    //   agentsLoading,
    //   teamsLoading,
    //   plannersLoading,
    //   agentsCount: agents.length,
    //   teamsCount: teams.length,
    //   plannersCount: planners.length,
    //   expandedRow
    // });

    if (selectedId && !agentsLoading && !teamsLoading && !plannersLoading) {
      let found = false;

      if (activeTab === 'agents' && agents.length > 0) {
        // console.log('Searching for agent with ID:', selectedId);
        // console.log('Available agents:', agents.map(a => ({ id: a.id, agent_id: a.agent_id, name: a.name })));
        const item = agents.find(item => {
          const a = item.config || item;
          return a.code === selectedId || a.agent_id === selectedId || a.id === selectedId;
        });
        if (item) {
          const agent = item.config || item;
          // console.log('Found agent:', agent);
          const agentKey = agent.code || agent.agent_id || agent.id;
          // console.log('Expanding agent row with key:', agentKey);
          setExpandedRow(agentKey);
          setExpandedRowTab('agents');
          const codeOrId = agent.code || agent.agent_id || agent.id;
          const loadingKey = `agent_${codeOrId}`;
          if (!entityGraphData[loadingKey] && !graphLoading[loadingKey]) {
            fetchEntityGraphData('agent', codeOrId);
          }
          found = true;
        } else {
          console.log('Agent not found in current page data');
        }
      } else if (activeTab === 'teams' && teams.length > 0) {
        // console.log('Searching for team with ID:', selectedId);
        // console.log('Available teams:', teams.map(t => ({ id: t.id, team_id: t.team_id, code: t.code, name: t.name })));
        const item = teams.find(item => {
          const t = item.config || item;
          return t.code === selectedId || t.team_id === selectedId || t.id === selectedId;
        });
        if (item) {
          const team = item.config || item;
          console.log('Found team:', team);
          const teamKey = team.code || team.team_id || team.id;
          // console.log('Expanding team row with key:', teamKey);
          setExpandedRow(teamKey);
          setExpandedRowTab('teams');
          const codeOrId = team.code || team.team_id || team.id;
          const loadingKey = `pipeline_${codeOrId}`;
          if (!entityGraphData[loadingKey] && !graphLoading[loadingKey]) {
            fetchEntityGraphData('pipeline', codeOrId);
          }
          found = true;
        } else {
          console.log('Team not found in current page data');
        }
      } else if (activeTab === 'planners' && planners.length > 0) {
        // console.log('Searching for planner with ID:', selectedId);
        // console.log('Available planners:', planners.map(p => ({ id: p.id, planner_id: p.planner_id, code: p.code, name: p.name })));
        const item = planners.find(item => {
          const p = item.config || item;
          return p.code === selectedId || p.planner_id === selectedId || p.id === selectedId;
        });
        if (item) {
          const planner = item.config || item;
          console.log('Found planner:', planner);
          const plannerKey = planner.code || planner.planner_id || planner.id;
          // console.log('Expanding planner row with key:', plannerKey);
          setExpandedRow(plannerKey);
          setExpandedRowTab('planners');
          const codeOrId = planner.code || planner.planner_id || planner.id;
          const loadingKey = `planner_${codeOrId}`;
          if (!entityGraphData[loadingKey] && !graphLoading[loadingKey]) {
            fetchEntityGraphData('planner', codeOrId);
          }
          found = true;
        } else {
          console.log('Planner not found in current page data');
        }
      }

      if (found) {
        console.log('Auto-expanded row for ID:', selectedId);
      } else {
        console.log('No matching entity found for ID:', selectedId);
      }
    }
  }, [selectedId, activeTab, agents, teams, planners, agentsLoading, teamsLoading, plannersLoading]);

  // Reset current page for the active tab
  useEffect(() => {
    if (activeTab === 'agents') {
      agentsPagination?.setCurrentPage(1);
    } else if (activeTab === 'agent-runs') {
      agentRunsPagination?.setCurrentPage(1);
    } else if (activeTab === 'teams') {
      teamsPagination?.setCurrentPage(1);
    } else if (activeTab === 'team-runs') {
      teamRunsPagination?.setCurrentPage(1);
    } else if (activeTab === 'planners') {
      plannersPagination?.setCurrentPage(1);
    } else if (activeTab === 'planner-runs') {
      plannerRunsPagination?.setCurrentPage(1);
    }
  }, [activeTab]);

  // Remove infinite scroll behavior

  const tabs = [
    { key: 'agents', label: 'Agents' },
    { key: 'agent-runs', label: 'Agent Runs' },
    { key: 'teams', label: 'Teams' },
    { key: 'team-runs', label: 'Team Runs' },
    { key: 'planners', label: 'Planners' },
    { key: 'planner-runs', label: 'Planner Runs' },
  ];

  // Fetch counts (runs counts are date-filtered)
  useEffect(() => {
    const fetchCounts = async () => {
      setCountsLoading(true);
      try {
        const [agentCount, teamCount, plannerCount, agentRunCount, teamRunCount, plannerRunCount] = await Promise.allSettled([
          getEntityCount('agents'),
          getEntityCount('pipeline'),
          getEntityCount('planners'),
          getObservabilityCount('agent', dateFilterMs),
          getObservabilityCount('pipeline', dateFilterMs),
          getObservabilityCount('planner', dateFilterMs)
        ]);

        const totalAgents = agentCount.status === 'fulfilled' ? agentCount.value : 0;
        const totalPipelines = teamCount.status === 'fulfilled' ? teamCount.value : 0;
        const totalPlanners = plannerCount.status === 'fulfilled' ? plannerCount.value : 0;
        const agentRunsTotal = agentRunCount.status === 'fulfilled' ? agentRunCount.value : 0;
        const pipelineRunsTotal = teamRunCount.status === 'fulfilled' ? teamRunCount.value : 0;
        const plannerRunsTotal = plannerRunCount.status === 'fulfilled' ? plannerRunCount.value : 0;

        setCounts({
          totalAgents,
          totalPipelines,
          totalPlanners,
          pipelineRunCount: pipelineRunsTotal,
          agentRunCount: agentRunsTotal,
          plannerRunCount: plannerRunsTotal
        });

        agentsPagination?.setToatalItems(totalAgents);
        teamsPagination?.setToatalItems(totalPipelines);
        plannersPagination?.setToatalItems(totalPlanners);
        agentRunsPagination?.setToatalItems(agentRunsTotal);
        teamRunsPagination?.setToatalItems(pipelineRunsTotal);
        plannerRunsPagination?.setToatalItems(plannerRunsTotal);
      } catch (error) {
        console.error('Error fetching counts:', error);
      } finally {
        setCountsLoading(false);
      }
    };
    fetchCounts();
  }, [dateFilterMs]);

  // Fetch Agents (numbered pagination)
  useEffect(() => {
    const fetchAgents = async () => {
      setAgentsLoading(true);
      try {
        const skip = ((agentsPagination?.currentPage || 1) - 1) * 10;
        const filter = { started_at: dateFilterMs.started_at };
        const sort = {};
        const query = { skip, limit: 10 };
        const response = await getAgentsPaginated(filter, sort, query);
        const agentsData = response?.data || response || [];
        setAgents(agentsData);
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };
    if (activeTab === 'agents') {
      fetchAgents();
    }
  }, [activeTab, agentsPagination?.currentPage, dateFilterMs]);

  // Fetch Agent Runs
  useEffect(() => {
    const fetchAgentRuns = async () => {
      setAgentRunsLoading(true);
      try {
        const skip = ((agentRunsPagination?.currentPage || 1) - 1) * 10;
        const body = {
          filter: { started_at: dateFilterMs.started_at },
          skip: skip,
          limit: 10
        };
        const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/agent', body);
        const runs = Array.isArray(data) ? data : [];
        // console.log('Agent runs fetched:', runs.length);
        setAgentRuns(runs);
      } catch (error) {
        console.error('Error fetching agent runs:', error);
        setAgentRuns([]);
      } finally {
        setAgentRunsLoading(false);
      }
    };
    if (activeTab === 'agent-runs') {
      fetchAgentRuns();
    }
  }, [activeTab, agentRunsPagination?.currentPage, dateFilterMs]);

  // Fetch Teams (numbered pagination)
  useEffect(() => {
    const fetchTeams = async () => {
      setTeamsLoading(true);
      try {
        const skip = ((teamsPagination?.currentPage || 1) - 1) * 10;
        const filter = { started_at: dateFilterMs.started_at };
        const sort = {};
        const query = { skip, limit: 10 };
        const response = await getPipelinesByPagination(filter, sort, query);
        const teamsData = response?.data || response || [];
        setTeams(teamsData);
      } catch (error) {
        console.error('Error fetching teams:', error);
        setTeams([]);
      } finally {
        setTeamsLoading(false);
      }
    };
    if (activeTab === 'teams') {
      fetchTeams();
    }
  }, [activeTab, teamsPagination?.currentPage, dateFilterMs]);

  // Fetch Team Runs
  useEffect(() => {
    const fetchTeamRuns = async () => {
      setTeamRunsLoading(true);
      try {
        const skip = ((teamRunsPagination?.currentPage || 1) - 1) * 10;
        const body = {
          filter: { started_at: dateFilterMs.started_at },
          skip: skip,
          limit: 10
        };
        const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/pipeline', body);
        const runs = Array.isArray(data) ? data : [];
        console.log('Team runs fetched:', runs.length);
        setTeamRuns(runs);
      } catch (error) {
        console.error('Error fetching team runs:', error);
        setTeamRuns([]);
      } finally {
        setTeamRunsLoading(false);
      }
    };
    if (activeTab === 'team-runs') {
      fetchTeamRuns();
    }
  }, [activeTab, teamRunsPagination?.currentPage, dateFilterMs]);

  // Fetch Planners (numbered pagination)
  useEffect(() => {
    const fetchPlanners = async () => {
      setPlannersLoading(true);
      try {
        const skip = ((plannersPagination?.currentPage || 1) - 1) * 10;
        const filter = { started_at: dateFilterMs.started_at };
        const sort = {};
        const query = { skip, limit: 10 };
        const response = await getPlannersPaginated(filter, sort, query);
        const plannersData = Array.isArray(response) ? response : response?.data || [];
        setPlanners(plannersData);
      } catch (error) {
        console.error('Error fetching planners:', error);
        setPlanners([]);
      } finally {
        setPlannersLoading(false);
      }
    };
    if (activeTab === 'planners') {
      fetchPlanners();
    }
  }, [activeTab, plannersPagination?.currentPage, dateFilterMs]);

  // Fetch Planner Runs
  useEffect(() => {
    const fetchPlannerRuns = async () => {
      setPlannerRunsLoading(true);
      try {
        const skip = ((plannerRunsPagination?.currentPage || 1) - 1) * 10;
        console.log('Fetching planner runs with skip:', skip, 'limit:', 10);
        const body = {
          filter: { started_at: dateFilterMs.started_at },
          skip: skip,
          limit: 10
        };
        const data = await post('/api/v1/mms_ai_metric_events/metrics/runs/planner', body);
        const runs = Array.isArray(data) ? data : [];
        console.log('Planner runs fetched:', runs.length, runs);
        setPlannerRuns(runs);
      } catch (error) {
        console.error('Error fetching planner runs:', error);
        setPlannerRuns([]);
      } finally {
        setPlannerRunsLoading(false);
      }
    };
    if (activeTab === 'planner-runs') {
      fetchPlannerRuns();
    }
  }, [activeTab, plannerRunsPagination?.currentPage, dateFilterMs]);

  const handleRowClick = (id) => {
    console.log('Row clicked:', id);
  };

  // Open run details in a new tab, preserving date filter if present
  const openRunDetails = (metricId) => {
    if (!metricId) return;
    const s = Number(searchParams.get('start'));
    const e = Number(searchParams.get('end'));
    const hasDates = Number.isFinite(s) && s > 0 && Number.isFinite(e) && e > 0;
    const qs = hasDates ? `?start=${s}&end=${e}` : '';
    const url = `/observability/details/${metricId}${qs}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Render accordion content with graphs
  const renderAccordionContent = (entityType, entityId, colSpan, entityName = '', aggregated = null) => {
    const loadingKey = `${entityType}_${entityId}`;
    const graphData = entityGraphData[loadingKey];
    const loading = graphLoading[loadingKey];

    // console.log('renderAccordionContent called:', {
    //   entityType,
    //   entityId,
    //   entityName,
    //   loadingKey,
    //   loading,
    //   hasGraphData: !!graphData,
    //   graphData
    // });

    if (loading) {
      return (
        <AccordionRow>
          <AccordionCell colSpan={colSpan}>
            <AccordionContent>
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Loading graph data...
              </div>
            </AccordionContent>
          </AccordionCell>
        </AccordionRow>
      );
    }

    if (!graphData) {
      return (
        <AccordionRow>
          <AccordionCell colSpan={colSpan}>
            <AccordionContent>
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                No graph data available
              </div>
            </AccordionContent>
          </AccordionCell>
        </AccordionRow>
      );
    }

    // Unified Chart.js analytics for agent/pipeline/planner
    if (graphData && graphData.joined) {
      const rows = graphData.joined;
      const tokensFromSplit = rows.reduce((s, r) => s + Number(r.total_tokens || 0), 0);
      const useRowTotal = (entityType === 'planner' || entityType === 'pipeline' || entityType === 'agent') && Number.isFinite(Number(aggregated?.total_tokens));
      const tokens = useRowTotal ? Number(aggregated.total_tokens) : tokensFromSplit;
      const execsFromSplit = rows.reduce((s, r) => s + Number(r.count || 0), 0);
      const execs = Number.isFinite(Number(aggregated?.total_runs)) ? Number(aggregated.total_runs) : execsFromSplit;
      const durations = rows.map(r => Number(r.avg_duration_ms || 0)).filter(n => n > 0);
      const avg = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;

      const labels = rows.map(r => r.name || r.code);
      const hasIO = rows.some(r => (Number(r.input_tokens || 0) > 0) || (Number(r.output_tokens || 0) > 0));
      const tokenBarData = (entityType === 'planner' || !hasIO)
        ? {
            labels,
            datasets: [
              { label: 'Total Tokens', data: rows.map(r => r.total_tokens || 0), backgroundColor: '#6366f1' }
            ]
          }
        : {
            labels,
            datasets: [
              { label: 'Input Tokens', data: rows.map(r => r.input_tokens || 0), backgroundColor: '#3b82f6' },
              { label: 'Output Tokens', data: rows.map(r => r.output_tokens || 0), backgroundColor: '#f97316' },
            ]
          };
      const countBarData = {
        labels,
        datasets: [
          { label: 'Execution Count', data: rows.map(r => r.count || 0), backgroundColor: '#10b981' }
        ]
      };
      const durationSorted = [...rows].sort((a,b) => (b.avg_duration_ms || 0) - (a.avg_duration_ms || 0));
      const durationBarData = {
        labels: durationSorted.map(r => r.name || r.code),
        datasets: [
          { label: 'Avg Duration (ms)', data: durationSorted.map(r => r.avg_duration_ms || 0), backgroundColor: '#ef4444' }
        ]
      };
      const counts = rows.map(r => r.count || 0);
      const minC = Math.min(...counts, 0);
      const maxC = Math.max(...counts, 1);
      const scaleR = (c) => { const t = (c - minC) / (maxC - minC || 1); return 6 + t * 18; };
      const bubbleData = {
        datasets: [
          {
            label: 'Components',
            data: rows.map(r => ({
              x: r.avg_duration_ms || 0,
              y: r.total_tokens || 0,
              r: scaleR(r.count || 0),
              label: r.name || r.code,
              custom: {
                name: r.name || r.code,
                totalTokens: r.total_tokens || 0,
                inputTokens: r.input_tokens || 0,
                outputTokens: r.output_tokens || 0,
                count: r.count || 0,
                avgDurationMs: r.avg_duration_ms || 0
              }
            })),
            backgroundColor: 'rgba(99,102,241,0.7)'
          }
        ]
      };

      const durationBarOptions = { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { callback: (v) => `${Math.round(v/1000)}s` } } } };
      const tokenBarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: true } },
        scales: entityType !== 'planner' ? { x: { stacked: true }, y: { stacked: true } } : {}
      };
      const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } };
      const bubbleOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const c = ctx?.raw?.custom || {};
                const secs = Math.round((Number(c.avgDurationMs || 0)) / 1000);
                return [
                  `${c.name || ''}`,
                  `Total Tokens: ${Number(c.totalTokens || 0).toLocaleString()}`,
                  `Input Tokens: ${Number(c.inputTokens || 0).toLocaleString()}`,
                  `Output Tokens: ${Number(c.outputTokens || 0).toLocaleString()}`,
                  `Executions: ${Number(c.count || 0).toLocaleString()}`,
                  `Avg Duration: ${secs}s`
                ];
              }
            }
          }
        },
        scales: {
          x: { title: { display: true, text: 'Avg Duration (ms)' } },
          y: { title: { display: true, text: 'Total Tokens' } }
        }
      };

      return (
        <AccordionRow>
          <AccordionCell colSpan={colSpan}>
            <AccordionContent>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                <GraphCard>
                  <GraphTitle>Total Tokens</GraphTitle>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{Number(tokens).toLocaleString()}</div>
                </GraphCard>
                <GraphCard>
                  <GraphTitle>Total Executions</GraphTitle>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{Number(execs).toLocaleString()}</div>
                </GraphCard>
                <GraphCard>
                  <GraphTitle>Avg Latency</GraphTitle>
                  <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{Number(avg).toLocaleString()} ms</div>
                </GraphCard>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                {entityType !== 'agent' && (
                  <GraphCard>
                    <GraphTitle>Token Consumption Breakdown</GraphTitle>
                    <div style={{ width: '100%', height: 320 }}>
                      <Bar data={tokenBarData} options={tokenBarOptions} />
                    </div>
                  </GraphCard>
                )}
                <GraphCard>
                  <GraphTitle>Execution Count</GraphTitle>
                  <div style={{ width: '100%', height: 320 }}>
                    <Bar data={countBarData} options={barOptions} />
                  </div>
                </GraphCard>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                <GraphCard>
                  <GraphTitle>Average Duration Analysis</GraphTitle>
                  <div style={{ width: '100%', height: 360 }}>
                    <Bar data={durationBarData} options={durationBarOptions} />
                  </div>
                </GraphCard>
                {entityType !== 'agent' && (
                  <GraphCard>
                    <GraphTitle>Efficiency Bubble Scatter</GraphTitle>
                    <div style={{ width: '100%', height: 360 }}>
                      <Bubble data={bubbleData} options={bubbleOptions} />
                    </div>
                  </GraphCard>
                )}
              </div>
            </AccordionContent>
          </AccordionCell>
        </AccordionRow>
      );
    }

    return null;
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/observability')}>
          <ChevronLeft size={20} />
          Back
        </BackButton>
        <Title>Observability Details</Title>
        {searchParams.get('tab') && (
          <div style={{ marginLeft: 'auto', position: 'relative' }} ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker((v) => !v)}
              style={{
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#ffffff',
                cursor: 'pointer',
                minWidth: 220,
                textAlign: 'left',
                marginRight: 12
              }}
            >
              {rangeLabel}
            </button>
            {showDatePicker && (
              <div style={{ position: 'absolute', right: 0, zIndex: 30 }}>
                <DateRangePicker
                  ranges={draftRange}
                  onChange={(item) => setDraftRange([item.selection])}
                  months={1}
                  direction="horizontal"
                  rangeColors={["#0ea5e9"]}
                  showDateDisplay={true}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#ffffff', border: '1px solid #e5e7eb', borderTop: 'none', padding: '8px 12px', borderRadius: '0 0 8px 8px' }}>
                  <button
                    onClick={() => { setShowDatePicker(false); setDraftRange(appliedRange); }}
                    style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, background: '#ffffff', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setAppliedRange(draftRange);
                      setShowDatePicker(false);
                      try {
                        agentsPagination?.setCurrentPage(1);
                        agentRunsPagination?.setCurrentPage(1);
                        teamsPagination?.setCurrentPage(1);
                        teamRunsPagination?.setCurrentPage(1);
                        plannersPagination?.setCurrentPage(1);
                        plannerRunsPagination?.setCurrentPage(1);
                      } catch (e) {}

                      const s = new Date(draftRange?.[0]?.startDate || new Date()).getTime();
                      const e = new Date(draftRange?.[0]?.endDate || new Date()).getTime();
                      const params = new URLSearchParams(searchParams);
                      params.set('tab', activeTab);
                      params.set('start', String(s));
                      params.set('end', String(e));
                      setSearchParams(params);
                    }}
                    style={{ padding: '6px 10px', border: '1px solid #0ea5e9', color: '#0ea5e9', borderRadius: 6, background: '#ffffff', cursor: 'pointer' }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <SearchContainer style={{ marginLeft: searchParams.get('tab') ? 0 : 'auto' }}>
          <SearchInput
            type="text"
            placeholder="Search runs, session, agents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
      </Header>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '0 0 12px 0' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
          <strong style={{ color: '#111827', fontSize: 13, marginRight: 6 }}></strong>
          {countsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><InlineSpinner /><span style={{ color: '#6b7280', fontSize: 13 }}>Loading...</span></div>
          ) : (
            <>
              <span style={{ color: '#374151', fontSize: 13 }}>Agents: <strong>{counts.totalAgents}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Pipelines: <strong>{counts.totalPipelines}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Planners: <strong>{counts.totalPlanners}</strong></span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
          <strong style={{ color: '#111827', fontSize: 13, marginRight: 6 }}>Runs (filtered):</strong>
          {countsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><InlineSpinner /><span style={{ color: '#6b7280', fontSize: 13 }}>Loading...</span></div>
          ) : (
            <>
              <span style={{ color: '#374151', fontSize: 13 }}>Pipeline: <strong>{counts.pipelineRunCount}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Agent: <strong>{counts.agentRunCount}</strong></span>
              <span style={{ color: '#e5e7eb' }}>|</span>
              <span style={{ color: '#374151', fontSize: 13 }}>Planner: <strong>{counts.plannerRunCount}</strong></span>
            </>
          )}
        </div>
      </div>

      <TabsContainer>
        <TabList>
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              active={activeTab === tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setSearchParams({});
                setExpandedRow(null);
              }}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabContent active={activeTab === 'agents'} ref={(el) => activeTab === 'agents' && setScrollContainerRef(el)}>
          <Table>
            <thead>
              <tr>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Bot size={14} />
                    <span>Agent</span>
                  </span>
                </TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Total Runs</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>Success Rate</TableHeader>
                <TableHeader>Pipelines</TableHeader>
                <TableHeader>Planners</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {agentsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading agents...
                  </TableCell>
                </TableRow>
              ) : agents.filter(item => {
                const agent = item.config || item;
                return !searchQuery ||
                  (agent.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (agent.description || '').toLowerCase().includes(searchQuery.toLowerCase());
              }).map((item) => {
                const agent = item.config || item;
                const agentKey = agent.code || agent.agent_id || agent.id;
                return (
                  <React.Fragment key={agent.id}>
                    <TableRow onClick={() => handleRowToggle('agent', agent.id, agent.code || agent.agent_id)} style={{ cursor: 'pointer' }}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ExpandIcon expanded={expandedRow === agentKey}>▼</ExpandIcon>
                          <div>
                            <div style={{ fontWeight: '500' }}>{agent.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{agent.code || agent.agent_id || agent.id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{agent.description || 'N/A'}</TableCell>
                      <TableCell>{item.total_runs?.toLocaleString() || 0}</TableCell>
                      <TableCell>{item.total_tokens?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <StatusBadge status={item.success_rate >= 80 ? 'Active' : 'Failed'}>
                          {item.success_rate || 0}%
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {item.pipeline_names && item.pipeline_names.length > 0 ? (
                          <div style={{ fontSize: '0.85rem' }}>
                            {item.pipeline_names.slice(0, 2).map((name, idx) => (
                              <div key={idx} style={{ color: '#6b7280' }}>{name}</div>
                            ))}
                            {item.pipeline_names.length > 2 && (
                              <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>+{item.pipeline_names.length - 2} more</div>
                            )}
                          </div>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {item.planner_names && item.planner_names.length > 0 ? (
                          <div style={{ fontSize: '0.85rem' }}>
                            {item.planner_names.slice(0, 2).map((name, idx) => (
                              <div key={idx} style={{ color: '#6b7280' }}>{name}</div>
                            ))}
                            {item.planner_names.length > 2 && (
                              <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>+{item.planner_names.length - 2} more</div>
                            )}
                          </div>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                          onClick={() => handleRowToggle('agent', agent.id, agent.code || agent.agent_id)}
                        >
                          {expandedRow === agentKey && expandedRowTab === 'agents' ? 'Collapse' : 'Details'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedRow === agentKey && expandedRowTab === 'agents' && renderAccordionContent('agent', agent.code || agent.agent_id || agent.id, 8, agent.name, { total_runs: item.total_runs, total_tokens: item.total_tokens, average_execution_time: item.average_execution_time })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={agentsPagination?.currentPage}
            totalPages={agentsPagination?.totalPages}
            totalCount={agentsPagination?.totalItems}
            setCurrentPage={agentsPagination?.setCurrentPage}
            setTotalPages={agentsPagination?.setTotalPages}
          />
        </TabContent>

        <TabContent active={activeTab === 'agent-runs'}>
          <Table>
            <thead>
              <tr>
                <TableHeader>Session ID</TableHeader>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Bot size={14} />
                    <span>Agent</span>
                  </span>
                </TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Started At</TableHeader>
                <TableHeader>Duration (ms)</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>LLM Model</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {agentRunsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading agent runs...
                  </TableCell>
                </TableRow>
              ) : agentRuns.filter(run =>
                !searchQuery ||
                (run.agent_session_id || run.run_id || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (run.metric_type_name || '').toLowerCase().includes(searchQuery.toLowerCase())
              ).map((run) => {
                const agentName = run.metric_type_name || '';
                const llmModel = (run.run_data && run.run_data.model) || run.llm_model || 'N/A';
                const metricId = run.metric_id || run.id;
                return (
                  <TableRow key={run.id || run.agent_session_id} onClick={() => openRunDetails(metricId)}>
                    <TableCell>{run.agent_session_id || run.run_id}</TableCell>
                    <TableCell>{agentName}</TableCell>
                    <TableCell>
                      <StatusBadge status={run.status}>{run.status}</StatusBadge>
                    </TableCell>
                    <TableCell>{formatRelativeTime(run.started_at)}</TableCell>
                    <TableCell>{run.duration_ms != null ? run.duration_ms : 'N/A'}</TableCell>
                    <TableCell>{run.total_tokens || 'N/A'}</TableCell>
                    <TableCell>
                      <span style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid #e2e8f0', fontSize: '0.75rem' }}>
                        {llmModel}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openRunDetails(metricId);
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={agentRunsPagination?.currentPage}
            totalPages={agentRunsPagination?.totalPages}
            totalCount={agentRunsPagination?.totalItems}
            setCurrentPage={agentRunsPagination?.setCurrentPage}
            setTotalPages={agentRunsPagination?.setTotalPages}
          />
        </TabContent>

        <TabContent active={activeTab === 'teams'} ref={(el) => activeTab === 'teams' && setScrollContainerRef(el)}>
          <Table>
            <thead>
              <tr>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Network size={14} />
                    <span>Pipeline/Team</span>
                  </span>
                </TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Total Runs</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>Success Rate</TableHeader>
                <TableHeader>Avg Execution Time</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {teamsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading teams...
                  </TableCell>
                </TableRow>
              ) : teams.filter(item => {
                const team = item.config || item;
                return !searchQuery ||
                  (team.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (team.description || '').toLowerCase().includes(searchQuery.toLowerCase());
              }).map((item) => {
                const team = item.config || item;
                const teamKey = team.code || team.team_id || team.id;
                return (
                  <React.Fragment key={team.id}>
                    <TableRow onClick={() => handleRowToggle('pipeline', team.id, team.code || team.team_id)} style={{ cursor: 'pointer' }}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ExpandIcon expanded={expandedRow === teamKey}>▼</ExpandIcon>
                          <div>
                            <div style={{ fontWeight: '500' }}>{team.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{team.code || team.team_id || team.id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{team.description || 'N/A'}</TableCell>
                      <TableCell>{item.total_runs?.toLocaleString() || 0}</TableCell>
                      <TableCell>{item.total_tokens?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <StatusBadge status={item.success_rate >= 80 ? 'Active' : 'Failed'}>
                          {item.success_rate || 0}%
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {item.average_execution_time ? `${(item.average_execution_time / 1000).toFixed(2)}s` : 'N/A'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                          onClick={() => handleRowToggle('pipeline', team.id, team.code || team.team_id)}
                        >
                          {expandedRow === teamKey && expandedRowTab === 'teams' ? 'Collapse' : 'Details'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedRow === teamKey && expandedRowTab === 'teams' && renderAccordionContent('pipeline', team.code || team.team_id || team.id, 7, team.name, { total_runs: item.total_runs, total_tokens: item.total_tokens, average_execution_time: item.average_execution_time })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={teamsPagination?.currentPage}
            totalPages={teamsPagination?.totalPages}
            totalCount={teamsPagination?.totalItems}
            setCurrentPage={teamsPagination?.setCurrentPage}
            setTotalPages={teamsPagination?.setTotalPages}
          />
        </TabContent>

        <TabContent active={activeTab === 'team-runs'}>
          <Table>
            <thead>
              <tr>
                <TableHeader>Run ID</TableHeader>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Network size={14} />
                    <span>Pipeline</span>
                  </span>
                </TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Started At</TableHeader>
                <TableHeader>Duration (ms)</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {teamRunsLoading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading team runs...
                  </TableCell>
                </TableRow>
              ) : teamRuns.filter(run =>
                !searchQuery ||
                (run.team_run_id || run.run_id || '').toLowerCase().includes(searchQuery.toLowerCase())
              ).map((run) => {
                const pipelineName = run.metric_type_name || '';
                const metricId = run.metric_id || run.id;
                return (
                  <TableRow key={run.id || run.team_run_id} onClick={() => openRunDetails(metricId)}>
                    <TableCell>{run.team_run_id || run.run_id}</TableCell>
                    <TableCell>{pipelineName}</TableCell>
                    <TableCell>
                      <StatusBadge status={run.status}>{run.status}</StatusBadge>
                    </TableCell>
                    <TableCell>{formatRelativeTime(run.started_at)}</TableCell>
                    <TableCell>{run.duration_ms != null ? run.duration_ms : 'N/A'}</TableCell>
                    <TableCell>{run.total_tokens || 'N/A'}</TableCell>
                    <TableCell>
                      <Button
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openRunDetails(metricId);
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={teamRunsPagination?.currentPage}
            totalPages={teamRunsPagination?.totalPages}
            totalCount={teamRunsPagination?.totalItems}
            setCurrentPage={teamRunsPagination?.setCurrentPage}
            setTotalPages={teamRunsPagination?.setTotalPages}
          />
        </TabContent>

        <TabContent active={activeTab === 'planners'} ref={(el) => activeTab === 'planners' && setScrollContainerRef(el)}>
          <Table>
            <thead>
              <tr>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Workflow size={14} />
                    <span>Planner</span>
                  </span>
                </TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Total Runs</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>Success Rate</TableHeader>
                <TableHeader>Pipelines</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {plannersLoading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading planners...
                  </TableCell>
                </TableRow>
              ) : planners.filter(item => {
                const planner = item.config || item;
                return !searchQuery ||
                  (planner.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (planner.description || '').toLowerCase().includes(searchQuery.toLowerCase());
              }).map((item) => {
                const planner = item.config || item;
                const plannerKey = planner.code || planner.planner_id || planner.id;
                return (
                  <React.Fragment key={planner.id}>
                    <TableRow onClick={() => handleRowToggle('planner', planner.id, planner.code || planner.planner_id)} style={{ cursor: 'pointer' }}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ExpandIcon expanded={expandedRow === plannerKey}>▼</ExpandIcon>
                          <div>
                            <div style={{ fontWeight: '500' }}>{planner.name}</div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{planner.code || planner.planner_id || planner.id}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{planner.description || 'N/A'}</TableCell>
                      <TableCell>{item.total_runs?.toLocaleString() || 0}</TableCell>
                      <TableCell>{item.total_tokens?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <StatusBadge status={item.success_rate >= 80 ? 'Active' : 'Failed'}>
                          {item.success_rate || 0}%
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {item.pipeline_names && item.pipeline_names.length > 0 ? (
                          <div style={{ fontSize: '0.85rem' }}>
                            {item.pipeline_names.slice(0, 2).map((name, idx) => (
                              <div key={idx} style={{ color: '#6b7280' }}>{name}</div>
                            ))}
                            {item.pipeline_names.length > 2 && (
                              <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>+{item.pipeline_names.length - 2} more</div>
                            )}
                          </div>
                        ) : 'N/A'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                          onClick={() => handleRowToggle('planner', planner.id, planner.code || planner.planner_id)}
                        >
                          {expandedRow === plannerKey && expandedRowTab === 'planners' ? 'Collapse' : 'Details'}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expandedRow === plannerKey && expandedRowTab === 'planners' && renderAccordionContent('planner', planner.code || planner.planner_id || planner.id, 7, planner.name, { total_runs: item.total_runs, total_tokens: item.total_tokens, average_execution_time: item.average_execution_time })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={plannersPagination?.currentPage}
            totalPages={plannersPagination?.totalPages}
            totalCount={plannersPagination?.totalItems}
            setCurrentPage={plannersPagination?.setCurrentPage}
            setTotalPages={plannersPagination?.setTotalPages}
          />
        </TabContent>

        <TabContent active={activeTab === 'planner-runs'}>
          <Table>
            <thead>
              <tr>
                <TableHeader>Planner Run ID</TableHeader>
                <TableHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Workflow size={14} />
                    <span>Planner</span>
                  </span>
                </TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Started At</TableHeader>
                <TableHeader>Duration (ms)</TableHeader>
                <TableHeader>Total Tokens</TableHeader>
                <TableHeader>LLM Model</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {plannerRunsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    Loading planner runs...
                  </TableCell>
                </TableRow>
              ) : plannerRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    No planner runs found
                  </TableCell>
                </TableRow>
              ) : plannerRuns.filter(run =>
                !searchQuery ||
                (run.planner_run_id || run.run_id || '').toLowerCase().includes(searchQuery.toLowerCase())
              ).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    No results match your search
                  </TableCell>
                </TableRow>
              ) : plannerRuns.filter(run =>
                !searchQuery ||
                (run.planner_run_id || run.run_id || '').toLowerCase().includes(searchQuery.toLowerCase())
              ).map((run) => {
                // console.log('Rendering planner run:', run);
                const plannerName = run.metric_type_name || '';
                const llmModel = (run.run_data && run.run_data.model) || run.llm_model || 'N/A';
                const metricId = run.metric_id || run.id;
                return (
                  <TableRow key={run.id || run.planner_run_id || Math.random()} onClick={() => openRunDetails(metricId)}>
                    <TableCell>{run.planner_run_id || run.run_id || 'N/A'}</TableCell>
                    <TableCell>{plannerName || 'N/A'}</TableCell>
                    <TableCell>
                      <StatusBadge status={run.status || 'Unknown'}>{run.status || 'Unknown'}</StatusBadge>
                    </TableCell>
                    <TableCell>{formatRelativeTime(run.started_at)}</TableCell>
                    <TableCell>{run.duration_ms != null ? run.duration_ms : 'N/A'}</TableCell>
                    <TableCell>{run.total_tokens || 'N/A'}</TableCell>
                    <TableCell>
                      <span style={{ padding: '2px 8px', borderRadius: '999px', border: '1px solid #e2e8f0', fontSize: '0.75rem' }}>
                        {llmModel}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openRunDetails(metricId);
                        }}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={plannerRunsPagination?.currentPage}
            totalPages={plannerRunsPagination?.totalPages}
            totalCount={plannerRunsPagination?.totalItems}
            setCurrentPage={plannerRunsPagination?.setCurrentPage}
            setTotalPages={plannerRunsPagination?.setTotalPages}
          />
        </TabContent>
      </TabsContainer>
    </Container>
  );
};

export default ObservabilityDetails;
